import { LargeTextInput } from "@/elements/LargeTextInput"
import { TextInput } from "@/elements/TextInput"
import useOutsideClick from "@/hooks/useOutsideClick"
import { useState, forwardRef, Ref, useEffect, useRef } from "react"
import { DropdownMenu } from "./DropdownMenu"
import clsx from "clsx"
import { TextVariant } from "@/theme"

type InputProps = React.InputHTMLAttributes<HTMLInputElement>

export type DropDownItem = {
  display: string
  [key: string]: string
}

interface Props {
  inputProps: InputProps
  label?: string
  size?: "small" | "large"
  inputValue: string
  id: string
  dropdownItems: DropDownItem[]
  onClickDropDownItem: (item: DropDownItem) => void
  dropdownPosition?: "above" | "below"
  onCloseDropdown?: () => void
  nextInputId?: string
  closeMenuOnSelect?: boolean
  className?: string
  showMenuWithoutValue?: boolean
  dropDownClassName?: string
  labelVariant?: TextVariant
  labelClassName?: string
}

export const TextInputWithDropdown = forwardRef<HTMLInputElement, Props>(
  (
    {
      inputProps,
      id,
      label,
      size = "small",
      inputValue,
      dropdownItems,
      dropDownClassName,
      onClickDropDownItem,
      dropdownPosition = "below",
      onCloseDropdown,
      closeMenuOnSelect = true,
      className,
      showMenuWithoutValue = false,
      labelVariant,
      labelClassName,
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    const handleFocus = () => {
      setIsFocused(true)
    }

    const handleBlur = () => {
      setTimeout(() => setIsFocused(false), 50)
    }

    useOutsideClick(id, () => {
      setMenuOpen(false)
    })

    useEffect(() => {
      // Add event listener to handle tabbing out of the dropdown menu
      document.addEventListener("keydown", e => {
        if (e.key === "Tab") {
          const focusedElement = document.activeElement
          if (focusedElement && !focusedElement.classList.contains("menu-button")) {
            onTabOut?.()
          }
        }
      })

      return () => {
        document.removeEventListener("keydown", () => {})
      }
    })

    useEffect(() => {
      if (showMenuWithoutValue && !inputValue && isFocused) {
        setMenuOpen(true)
      } else if (isFocused && inputValue?.length > 0 && dropdownItems?.length > 0) {
        setMenuOpen(true)
      } else if (inputValue?.length === 0 && !showMenuWithoutValue) {
        setMenuOpen(false)
      }
    }, [isFocused, inputValue, dropdownItems, showMenuWithoutValue])

    const onClickDropdownMenuItem = (item: DropDownItem) => {
      onClickDropDownItem(item)
      if (closeMenuOnSelect) {
        onCloseDropdown?.()
        if (ref && "current" in ref && ref.current) {
          ref.current.blur()
        }
        setTimeout(() => {
          setMenuOpen(false)
        }, 100)
      }
    }

    const onTabOut = () => {
      onCloseDropdown?.()
      setMenuOpen(false)
    }

    return (
      <div className={clsx("relative", className)} id={id}>
        {size === "large" ? (
          <LargeTextInput
            {...inputProps}
            id={id}
            autoFocus={false}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={inputProps.onChange}
            value={inputValue}
            ref={ref as Ref<HTMLInputElement>}
            autoComplete="off"
          />
        ) : (
          <TextInput
            {...inputProps}
            id={id}
            label={label}
            labelVariant={labelVariant}
            labelClassName={labelClassName}
            autoFocus={false}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={inputProps.onChange}
            value={inputValue}
            ref={ref as Ref<HTMLInputElement>}
            autoComplete="off"
          />
        )}
        {menuOpen && (
          <DropdownMenu
            className={dropDownClassName}
            dropdownPosition={dropdownPosition}
            items={dropdownItems}
            onClickItem={onClickDropdownMenuItem}
            onTabOut={onTabOut}
            searchString={inputValue}
          />
        )}
      </div>
    )
  }
)

TextInputWithDropdown.displayName = "TextInputWithDropdown"
